import { useMemo } from 'react';
import type { Category } from '@services/knowledge.service';
import { CARD, CARD_CLASS, CardFoot } from './shell';
import { coverageStats, emptyCategories, leastRead } from './coverage';
import { daysSince, isStale, type ArticleRow } from './types';

// 18c — the supply side of Insights, below the gap queue.
//
// The artboard's own note says its two panels exist because search queries are
// not logged. Here they are, so the gap queue above answers the demand
// question and these answer the supply one. Keeping both is the point: a gap
// queue tells you what to write next, and these tell you what you already
// wrote that nobody wants and which shelves you promised and left bare.

const MONO = 'font-mono';

function Stat({
  label,
  value,
  warn,
  title,
}: {
  label: string;
  value: string;
  warn?: boolean;
  title?: string;
}) {
  return (
    <div className={`${CARD_CLASS} min-w-0 flex-1 px-[18px] py-4`} style={CARD} title={title}>
      <div
        className={`${MONO} text-[26px] font-semibold tabular-nums`}
        style={{ color: warn ? 'var(--co-terracotta)' : 'var(--co-ink)' }}
      >
        {value}
      </div>
      <div
        className={`${MONO} mt-2 text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]`}
      >
        {label}
      </div>
    </div>
  );
}

function PanelHead({ title, blurb }: { title: string; blurb: string }) {
  return (
    <div className="flex-none border-b border-[var(--co-line)] px-5 pb-[14px] pt-4">
      <h3 className="text-[19px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
        {title}
      </h3>
      <p className="mt-[5px] text-[12.5px] leading-[1.5] text-[var(--co-ink-3)]">{blurb}</p>
    </div>
  );
}

export function CoveragePanels({
  rows,
  tree,
  categoryCount,
  onWriteFor,
  onOpenArticle,
}: {
  rows: ArticleRow[];
  tree: Category[];
  categoryCount: number;
  /** Start an article already filed under the empty category. */
  onWriteFor: (categoryId: string) => void;
  onOpenArticle: (id: string) => void;
}) {
  const empties = useMemo(() => emptyCategories(tree), [tree]);
  const quiet = useMemo(() => leastRead(rows), [rows]);
  const stats = useMemo(
    () => coverageStats(rows, empties, categoryCount),
    [rows, empties, categoryCount],
  );

  return (
    <>
      <div className="grid flex-none grid-cols-2 gap-[14px] md:grid-cols-4">
        {stats.map((stat) => (
          <Stat key={stat.label} {...stat} />
        ))}
      </div>

      <div className="flex min-h-0 flex-1 gap-[14px]">
        <div
          className={`${CARD_CLASS} flex min-w-0 flex-1 flex-col overflow-hidden`}
          style={CARD}
        >
          <PanelHead
            title="Categories with nothing in them"
            blurb="Somebody created these and nothing fills them. Each one is a commissioning brief."
          />
          <div className="min-h-0 flex-1">
            {empties.length === 0 ? (
              <p className="px-5 py-8 text-[12.5px] leading-[1.6] text-[var(--co-ink-3)]">
                Every category has at least one article in it. Nothing here is an empty promise to
                a reader.
              </p>
            ) : (
              empties.map((cat) => (
                <div
                  key={cat.id}
                  className="flex items-center gap-3 border-b border-[var(--co-line-subtle)] px-5 py-[14px] last:border-b-0"
                >
                  <span
                    className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-[7px] border border-dashed border-[var(--co-line-strong)] text-[11px] text-[var(--co-ink-4)]"
                    aria-hidden="true"
                  >
                    ◇
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13.5px] font-semibold text-[var(--co-ink)]">
                      {cat.name}
                    </div>
                    <div className={`${MONO} mt-[3px] truncate text-[10.5px] text-[var(--co-ink-4)]`}>
                      {[`/${cat.slug}`, cat.parentName && `UNDER ${cat.parentName.toUpperCase()}`]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  <span
                    className={`${MONO} inline-flex flex-none items-center whitespace-nowrap rounded-[5px] border px-2 py-[3px] text-[9.5px] font-semibold uppercase tracking-[.1em]`}
                    style={{
                      borderColor: 'var(--co-terracotta-line)',
                      background: 'var(--co-terracotta-wash)',
                      color: 'var(--co-terracotta)',
                    }}
                  >
                    No articles yet
                  </span>
                  {/* The verb is the point of the panel: the row is not a
                      complaint, it is a brief you can accept from here. */}
                  <button
                    type="button"
                    onClick={() => onWriteFor(cat.id)}
                    className={`${MONO} flex h-[30px] flex-none items-center whitespace-nowrap rounded-[7px] border border-[var(--co-brass-line)] bg-[var(--co-brass-wash)] px-3 text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--co-brass-ink)] hover:border-[var(--co-brass)]`}
                  >
                    Write one →
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div
          className={`${CARD_CLASS} flex w-[520px] flex-none flex-col overflow-hidden`}
          style={CARD}
        >
          <PanelHead
            title="Articles nobody reads"
            blurb="Mis-titled, mis-categorised, or unnecessary — a judgement you make from here."
          />
          <div className="min-h-0 flex-1">
            {quiet.length === 0 ? (
              <p className="px-5 py-8 text-[12.5px] leading-[1.6] text-[var(--co-ink-3)]">
                No published articles to rank yet.
              </p>
            ) : (
              quiet.map((row) => {
                const stale = isStale(row.updatedAt);
                const days = daysSince(row.updatedAt);
                return (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => onOpenArticle(row.id)}
                    className="flex w-full items-center gap-[14px] border-b border-[var(--co-line-subtle)] px-5 py-[14px] text-left last:border-b-0 hover:bg-[var(--co-selected)]"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-semibold text-[var(--co-ink)]">
                        {row.title}
                      </div>
                      {/* Views alone cannot separate "nobody wants this" from
                          "nobody has been told about it". The review age is
                          the second axis that does. */}
                      <div
                        className={`${MONO} mt-1 text-[10px] font-semibold uppercase tracking-[.08em]`}
                        style={{ color: stale ? 'var(--co-terracotta)' : 'var(--co-ink-4)' }}
                      >
                        {`Last reviewed ${days} ${days === 1 ? 'day' : 'days'} ago`}
                      </div>
                    </div>
                    <div className="flex-none text-right">
                      <div
                        className={`${MONO} text-[16px] font-semibold tabular-nums text-[var(--co-ink)]`}
                      >
                        {row.views.toLocaleString()}
                      </div>
                      <div
                        className={`${MONO} mt-[3px] text-[9px] uppercase tracking-[.1em] text-[var(--co-ink-4)]`}
                      >
                        {row.views === 1 ? 'view' : 'views'}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
          <CardFoot left="Fewest views first" />
        </div>
      </div>
    </>
  );
}
